/// <reference types="vite/client" />
import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { formatFileSize } from '../../utils/formatUtils';
import apiService from '../../services/apiService';

// Get API base URL from service
const API_BASE = apiService.baseURL;

interface FileObject {
    name: string;
    size: number;
    type: string;
    [key: string]: any;
}

interface MergeOptionsData {
    fileIds: string[];
    outputFormat: string;
    _reorderedFiles?: FileObject[];
    [key: string]: any;
}

interface AvailableFormats {
    formats: string[];
    defaultFormat: string;
    allowSelection: boolean;
    message: string;
}

interface MergeOptionsProps {
    files: FileObject[];
    options: MergeOptionsData;
    onChange: (options: MergeOptionsData) => void;
    onProcess: () => void;
    onBack: () => void;
    loading: boolean;
}

const MergeOptions: React.FC<MergeOptionsProps> = ({
    files,
    options,
    onChange,
    onProcess,
    onBack,
    loading
}) => {
    // State for available formats from backend
    const [availableFormats, setAvailableFormats] = useState<AvailableFormats>({
        formats: ["pdf"],
        defaultFormat: "pdf",
        allowSelection: false,
        message: "All files will be merged into a PDF document."
    });

    // Loading state for formats check
    const [checkingFormats, setCheckingFormats] = useState(false);

    // Ref to keep track of the fileIds we've already fetched for
    const processedFileIdsRef = useRef<string | null>(null);

    // Add state for tracking request status
    const [formatRequestSent, setFormatRequestSent] = useState(false);

    // Add useRef for debounce timer
    const formatTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleOptionChange = (name: string, value: any) => {
        // Don't modify fileIds if it's already set
        const newOptions = {
            ...options,
            [name]: value
        };

        // Ensure fileIds is preserved
        if (!newOptions.fileIds && options.fileIds) {
            newOptions.fileIds = options.fileIds;
        }

        onChange(newOptions);
    };

    // Fetch available formats when fileIds change
    useEffect(() => {
        // Skip if we don't have files
        if (!files || files.length === 0) return;

        // Skip if already sent a request and we're still waiting
        if (formatRequestSent && checkingFormats) {
            return;
        }

        // Get current fileIds
        const fileIds = options.fileIds || [];

        // Skip if we don't have fileIds
        if (!fileIds || fileIds.length === 0) return;

        // Convert fileIds array to string for comparison
        const fileIdsString = [...fileIds].sort().join(',');

        // Skip if we've already processed these exact fileIds
        if (processedFileIdsRef.current === fileIdsString) {
            return;
        }

        // Clear any existing timer
        if (formatTimerRef.current) {
            clearTimeout(formatTimerRef.current);
        }

        // Set a new timer to debounce multiple calls
        formatTimerRef.current = setTimeout(() => {
            const getAvailableFormats = async () => {
                // Skip if we're already checking formats
                if (checkingFormats) {
                    return;
                }

                setCheckingFormats(true);
                setFormatRequestSent(true);

                try {
                    // Format payload correctly for FastAPI Body parameter
                    const requestBody = JSON.stringify(fileIds);

                    // Call the API endpoint
                    const response = await fetch(`${API_BASE}/api/tools/operations/available-formats`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: requestBody
                    });

                    if (response.ok) {
                        const data: AvailableFormats = await response.json();
                        setAvailableFormats(data);

                        // Set the default format as the selected format
                        if (data.defaultFormat && data.defaultFormat !== options.outputFormat) {
                            handleOptionChange('outputFormat', data.defaultFormat);
                        }

                        // Remember that we've processed these fileIds
                        processedFileIdsRef.current = fileIdsString;
                    } else {
                        console.error('Failed to get available formats:', response.status, response.statusText);

                        // Default to only PDF if there's an error
                        setAvailableFormats({
                            formats: ["pdf"],
                            defaultFormat: "pdf",
                            allowSelection: false,
                            message: "All files will be merged into a PDF document."
                        });

                        if (options.outputFormat !== 'pdf') {
                            handleOptionChange('outputFormat', 'pdf');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching available formats:', error);
                    // Default to only PDF if there's an error
                    setAvailableFormats({
                        formats: ["pdf"],
                        defaultFormat: "pdf",
                        allowSelection: false,
                        message: "All files will be merged into a PDF document."
                    });

                    if (options.outputFormat !== 'pdf') {
                        handleOptionChange('outputFormat', 'pdf');
                    }
                } finally {
                    setCheckingFormats(false);
                    setFormatRequestSent(false);
                }
            };

            getAvailableFormats();
        }, 500);

        // Cleanup function to clear the timeout when the component unmounts or deps change
        return () => {
            if (formatTimerRef.current) {
                clearTimeout(formatTimerRef.current);
            }
        };
    }, [files, options.fileIds, checkingFormats, formatRequestSent]);

    const moveFile = (index: number, direction: 'up' | 'down') => {
        if ((direction === 'up' && index === 0) ||
            (direction === 'down' && index === files.length - 1)) {
            return;
        }

        const newFiles = [...files];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap files
        [newFiles[index], newFiles[swapIndex]] = [newFiles[swapIndex], newFiles[index]];

        // Get the current fileIds (ensure it exists and is an array)
        const fileIdsArray = Array.isArray(options.fileIds) ? [...options.fileIds] : [];

        // Only proceed with swapping fileIds if we have enough fileIds
        if (fileIdsArray.length >= 2 && fileIdsArray.length >= newFiles.length) {
            // Swap the fileIds in the same positions
            [fileIdsArray[index], fileIdsArray[swapIndex]] = [fileIdsArray[swapIndex], fileIdsArray[index]];
        }

        // Update in parent component - IMPORTANT: both files array and fileIds must be updated together
        onChange({
            ...options,
            _reorderedFiles: newFiles,
            fileIds: fileIdsArray
        });
    };

    // Format option labels
    const getFormatLabel = (format: string) => {
        switch (format) {
            case 'pdf': return 'PDF Document';
            case 'docx': return 'Word Document (DOCX)';
            case 'doc': return 'Word Document (DOC)';
            case 'txt': return 'Text Document';
            default: return format.toUpperCase();
        }
    };

    return (
        <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden dark:bg-slate-800 dark:border-slate-700">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 max-md:p-4">
                <h3 className="text-lg font-semibold text-slate-800 mt-0 mb-2 dark:text-slate-200">Files Order</h3>
                <p className="text-sm text-slate-500 mt-0 mb-4 dark:text-slate-400">Drag files to rearrange the order in which they will be merged.</p>

                <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                    {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3.5 bg-white border border-slate-200 rounded-lg dark:bg-slate-900 dark:border-slate-700">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <span className="flex items-center justify-center w-6 h-6 bg-primary text-white rounded-full text-xs font-semibold shrink-0">{index + 1}</span>
                                <div className="min-w-0 overflow-hidden">
                                    <div className="text-[15px] font-medium text-slate-800 whitespace-nowrap overflow-hidden text-ellipsis dark:text-slate-200">{file.name}</div>
                                    <div className="text-xs text-slate-500 dark:text-slate-400">
                                        {file.type} • {formatFileSize(file.size)}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-2 shrink-0">
                                <button
                                    className="flex items-center justify-center w-7 h-7 p-0 bg-slate-50 border border-slate-200 rounded-md text-slate-500 cursor-pointer transition-colors hover:bg-primary hover:text-white hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-primary dark:hover:text-white dark:hover:border-primary"
                                    onClick={() => moveFile(index, 'up')}
                                    disabled={index === 0 || loading}
                                    title="Move up"
                                    type="button"
                                >
                                    <ArrowUp size={16} />
                                </button>
                                <button
                                    className="flex items-center justify-center w-7 h-7 p-0 bg-slate-50 border border-slate-200 rounded-md text-slate-500 cursor-pointer transition-colors hover:bg-primary hover:text-white hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed dark:bg-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-primary dark:hover:text-white dark:hover:border-primary"
                                    onClick={() => moveFile(index, 'down')}
                                    disabled={index === files.length - 1 || loading}
                                    title="Move down"
                                    type="button"
                                >
                                    <ArrowDown size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="p-6 border-b border-slate-200 dark:border-slate-700 max-md:p-4">
                <h3 className="text-lg font-semibold text-slate-800 mt-0 mb-2 dark:text-slate-200">Merge Settings</h3>

                {/* Output format section */}
                <div className="mb-0">
                    <label className="block font-semibold text-slate-800 mb-1 dark:text-slate-200">Output Format</label>

                    {availableFormats.allowSelection ? (
                        <div className="flex flex-wrap gap-3 mt-3">
                            {availableFormats.formats.map(format => (
                                <label
                                    key={format}
                                    className={`flex items-center gap-2 px-3.5 py-2.5 bg-white border rounded-lg cursor-pointer transition-all hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-900 ${options.outputFormat === format ? 'bg-blue-50 border-primary dark:bg-blue-500/15 dark:border-primary' : 'border-slate-200 dark:border-slate-700'}`}
                                >
                                    <input
                                        type="radio"
                                        name="outputFormat"
                                        value={format}
                                        checked={options.outputFormat === format}
                                        onChange={() => handleOptionChange('outputFormat', format)}
                                        disabled={loading || checkingFormats}
                                        className="appearance-none w-[18px] h-[18px] border-2 border-slate-200 rounded-full m-0 cursor-pointer relative transition-all checked:border-primary checked:after:content-[''] checked:after:absolute checked:after:top-[3px] checked:after:left-[3px] checked:after:w-2 checked:after:h-2 checked:after:rounded-full checked:after:bg-primary dark:border-slate-700 dark:checked:border-primary"
                                    />
                                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{getFormatLabel(format)}</span>
                                </label>
                            ))}
                        </div>
                    ) : (
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mt-2 dark:bg-slate-800 dark:border-l-[3px] dark:border-l-blue-400 dark:border-slate-700">
                            <p className="m-0 text-sm text-slate-500 dark:text-slate-200">{availableFormats.message}</p>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-between p-6 gap-4 max-md:flex-col max-md:p-4">
                <button
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-transparent text-slate-500 border border-slate-300 rounded-lg font-medium cursor-pointer transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:opacity-70 disabled:cursor-not-allowed dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-900 dark:hover:text-slate-200 max-md:w-full"
                    onClick={onBack}
                    disabled={loading}
                    type="button"
                >
                    Back
                </button>

                <button
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white border-none rounded-lg font-medium cursor-pointer transition-all min-w-[180px] hover:bg-blue-700 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 max-md:w-full"
                    onClick={onProcess}
                    disabled={loading || files.length < 2 || checkingFormats}
                    type="button"
                >
                    {loading ? (
                        <>
                            <span className="inline-block w-4 h-4 border-2 border-white/30 rounded-full border-t-white animate-spin"></span>
                            Merging...
                        </>
                    ) : (
                        <>
                            Merge Files
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default MergeOptions;
